/**
 * KumagaiFeedbackPage — Kumagai 2000 cyano–stratification positive feedback
 *
 * Surface chlorophyll-a absorbs shortwave radiation in the top 0.5 m, intensifying near-surface
 * heating and reinforcing stratification, which favours buoyant cyanobacteria → more chl-a → loop.
 * The model computes a feedback amplification factor on the cyanobacteria growth rate.
 *
 * Reference: Kumagai, M., Nakano, S., Jiao, C., Hayakawa, K., Tsujimura, S., Nakajima, T.,
 *            Frenette, J.J. & Quesada, A. (2000) Limnology 1(3): 191-200, Lake Biwa Research Institute.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { FlaskConical, ArrowLeft, Info } from 'lucide-react';
import PageHeader from '@/components/PageHeader';
import { useModulesEnabled } from '@/lib/moduleRegistry';
import ModuleConflictBanner from '@/components/ModuleConflictBanner';

export default function KumagaiFeedbackPage() {
  const navigate = useNavigate();
  const { isEnabled, toggle } = useModulesEnabled();
  const enabled = isEnabled('kumagai_feedback');
  const [chl, setChl] = useState(25);          // µg/L total chl-a
  const [stratified, setStrat] = useState(true);
  const [surfDepth, setSurfDepth] = useState(0.5);  // m — top mixed sub-layer

  const R = useMemo(() => {
    // Light absorption in top 0.5 m by chl-a: k_chl ≈ 0.015 m⁻¹·(µg/L)⁻¹
    const k_chl = 0.015 * chl;            // m⁻¹
    const k_water = 0.27;
    const fracAbsorbed = 1 - Math.exp(-(k_chl + k_water) * surfDepth);
    // Surface heating rate boost (Kumagai 2000 calibration)
    // ΔT_extra ≈ 0.4 × fracAbsorbed during peak insolation
    const T_boost_C = stratified ? 0.4 * fracAbsorbed : 0;
    // Feedback amplification factor on cyano growth:
    //   A = 1 + 0.6 × tanh((chl - 10)/15)  during stratification
    //   A = 1                                during mixed
    const A = stratified ? 1 + 0.6 * Math.tanh(Math.max(0, (chl - 10) / 15)) : 1;
    return { k_chl, fracAbsorbed, T_boost_C, A };
  }, [chl, stratified, surfDepth]);

  const fmt = (n: number, d = 2) => isFinite(n) ? n.toFixed(d) : '—';

  return (
    <div className="space-y-4 p-4">
      <PageHeader icon={FlaskConical} title="Module — Kumagai Cyano–Stratification Feedback"
        subtitle="Lake Biwa Research Institute: positive feedback loop between cyanobacteria and stratification"
        actions={<div className="flex items-center gap-2 flex-wrap">
          <label className={`inline-flex items-center gap-2 rounded-lg border-2 px-3 py-1.5 text-sm font-semibold cursor-pointer ${enabled ? 'border-emerald-600 bg-emerald-900/30 text-emerald-200' : 'border-slate-700 bg-slate-800 text-slate-400'}`}>
            <input type="checkbox" checked={enabled} onChange={() => toggle('kumagai_feedback')} className="accent-emerald-500" /> Module {enabled ? 'ENABLED' : 'DISABLED'}
          </label>
          <button onClick={() => navigate('/modules')} className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700"><ArrowLeft className="w-4 h-4" /> Modules</button>
        </div>}
      />
      <ModuleConflictBanner moduleId="kumagai_feedback" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Light absorbed in top {surfDepth} m</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{(R.fracAbsorbed * 100).toFixed(0)} %</div>
          <div className="text-xs text-slate-500 mt-1">k_chl = {fmt(R.k_chl, 2)} m⁻¹</div>
        </div>
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-amber-300">Extra surface heating</div>
          <div className="text-2xl font-bold text-amber-200 mt-1">+{fmt(R.T_boost_C, 2)} °C</div>
          <div className="text-xs text-slate-500 mt-1">peak-insolation contribution</div>
        </div>
        <div className="rounded-xl border-2 border-red-700 bg-red-900/15 p-4">
          <div className="text-xs uppercase tracking-wider text-red-300">Cyano-growth amplification A</div>
          <div className="text-2xl font-bold text-red-200 mt-1">× {fmt(R.A, 2)}</div>
          <div className="text-xs text-slate-500 mt-1">{stratified ? 'feedback active' : 'mixed — no feedback'}</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs uppercase tracking-wider text-slate-400">Status</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{stratified ? 'STRATIFIED' : 'MIXED'}</div>
          <div className="text-xs text-slate-500 mt-1">aeration breaks the loop</div>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
        <div>
          <label className="text-slate-400 block mb-1">Total chl-a: <strong className="text-slate-100">{chl} µg/L</strong></label>
          <input type="range" min={0} max={80} step={1} value={chl} onChange={e => setChl(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="text-slate-400 block mb-1">Surface sub-layer: <strong className="text-slate-100">{surfDepth.toFixed(2)} m</strong></label>
          <input type="range" min={0.2} max={1.5} step={0.05} value={surfDepth} onChange={e => setSurfDepth(Number(e.target.value))} className="w-full accent-sky-500" />
        </div>
        <div>
          <label className="flex items-center gap-2 mt-4">
            <input type="checkbox" checked={stratified} onChange={e => setStrat(e.target.checked)} className="w-4 h-4 accent-amber-500" />
            <span className="text-slate-200">Reservoir stratified</span>
          </label>
        </div>
      </div>

      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4 text-xs text-slate-400 leading-relaxed">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2"><Info className="w-4 h-4 text-rb-brand" /> The feedback loop</div>
        <ol className="list-decimal ml-5 space-y-1">
          <li>Cyanobacteria reach the surface (buoyancy) and absorb solar radiation in the top 0.5 m.</li>
          <li>Near-surface heating intensifies → stratification strengthens (lower Brunt-Väisälä N).</li>
          <li>Stratification favours buoyant taxa (cyanobacteria) over diatoms / greens (need mixing).</li>
          <li>More cyanobacteria → more absorption → tighter loop.</li>
        </ol>
        <p className="mt-2">Aeration breaks the loop at step 2 (destratification → step 3 reverses). When this module is
        enabled, the Algal Dynamics page multiplies the cyanobacteria growth rate by amplification factor A during
        stratified periods, making the predicted bloom magnitude more realistic for very turbid (high-chl) starting conditions.</p>
        <p className="italic text-slate-500 mt-2">Reference: Kumagai et al. 2000 Limnology 1(3): 191-200, Lake Biwa Research Institute, Shiga, Japan.</p>
      </div>
    </div>
  );
}
